/**
 * MailComplaintNoticeToggle (OCP-621) - the customer's switch for the
 * once-per-month "a recipient marked your mail as spam" email to the
 * account address. Self-fetching so the overage settings form and this
 * switch fail independently.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, ApiError } from '../lib/api';
import { useMailWriteAccess } from '../lib/use-mail-write-access';

interface NoticeSettings {
  complaint_notices: boolean;
}

export function MailComplaintNoticeToggle() {
  const { t } = useTranslation('mail-quota');
  const [value, setValue] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { canWrite, readOnlyReason } = useMailWriteAccess();

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const r = await api<NoticeSettings>('/v1/portal/me/mail-notice-settings', { signal });
      setValue(r.complaint_notices);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setError(err instanceof ApiError ? t('complaintNotice.loadFailedHttp', { status: err.status }) : t('complaintNotice.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const toggle = async (next: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<NoticeSettings>('/v1/portal/me/mail-notice-settings', { method: 'PATCH', body: { complaint_notices: next } });
      setValue(r.complaint_notices);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? (readOnlyReason ?? t('complaintNotice.notAllowed')) : t('complaintNotice.saveFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="mail-complaint-notice-toggle" className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('complaintNotice.title')}</h3>
      <label className="mt-2 flex items-start gap-3 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          checked={value === true}
          disabled={value === null || busy || !canWrite}
          onChange={(e) => void toggle(e.target.checked)}
          className="mt-0.5"
        />
        <span>{t('complaintNotice.label')}</span>
      </label>
      {!canWrite && readOnlyReason && <p className="mt-2 text-xs text-gray-500">{readOnlyReason}</p>}
      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </section>
  );
}
